import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "../components/SEO";
import { galleriSubpages } from "../data/siteData";
import { useContactDrawer } from "../context/ContactDrawerContext";

export default function GalleryDetail() {
  const { gallerySlug } = useParams();
  const { openDrawer } = useContactDrawer();
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const gallery = galleriSubpages.find((g) => g.slug === gallerySlug);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxImage || !gallery?.images) return;
      if (e.key === "Escape") {
        setLightboxImage(null);
      } else if (e.key === "ArrowRight") {
        const nextIdx = (lightboxIndex + 1) % gallery.images.length;
        setLightboxIndex(nextIdx);
        setLightboxImage(gallery.images[nextIdx].src);
      } else if (e.key === "ArrowLeft") {
        const prevIdx = (lightboxIndex - 1 + gallery.images.length) % gallery.images.length;
        setLightboxIndex(prevIdx);
        setLightboxImage(gallery.images[prevIdx].src);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxImage, lightboxIndex, gallery]);

  if (!gallery) {
    return (
      <div className="pt-32 pb-20 min-h-[60vh] flex flex-col items-center justify-center bg-brand-sand-50 px-6">
        <SEO title="Galleri ikke fundet | Elmegaarden Have og Anlæg" />
        <h1 className="text-2xl md:text-3xl font-bold text-brand-green-900 mb-4">
          Galleriet blev ikke fundet
        </h1>
        <p className="text-brand-charcoal/70 mb-8">
          Vi kunne desværre ikke finde det ønskede galleri.
        </p>
        <Link
          to="/projekter"
          className="px-6 py-3 bg-brand-green-700 text-white font-bold text-sm tracking-wide rounded-sm hover:bg-brand-green-800 transition-colors"
        >
          Se alle gallerier
        </Link>
      </div>
    );
  }

  const primaryImage = gallery.ogImage || gallery.images?.[0]?.src;

  return (
    <div className="pt-[72px] bg-brand-sand-50 min-h-screen">
      <SEO
        title={gallery.title}
        description={gallery.metaDescription}
        ogImage={primaryImage}
        canonical={`/galleri/${gallery.slug}`}
      />

      {/* Header Banner */}
      <section className="bg-brand-green-950 text-white pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl text-left">
            <Link
              to="/projekter"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-sand-300 hover:text-white mb-6 transition-colors"
            >
              <i className="fa-solid fa-arrow-left text-[10px]" /> Alle gallerier
            </Link>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-white mb-4">
              {gallery.heading}
            </h1>
            {gallery.description && (
              <p className="text-neutral-200 text-base md:text-lg leading-relaxed font-light">
                {gallery.description}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          {/* Subgalleries selector pills */}
          <div className="flex flex-wrap gap-2 mb-12 justify-start border-b border-brand-sand-200 pb-6">
            <Link
              to="/projekter"
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors bg-white text-brand-charcoal hover:bg-brand-sand-100 border border-brand-sand-200"
            >
              Alle billeder
            </Link>
            {galleriSubpages.map((sub) => {
              const isCurrent = sub.slug === gallerySlug;
              return (
                <Link
                  key={sub.slug}
                  to={`/galleri/${sub.slug}`}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${
                    isCurrent
                      ? "bg-[#7ca632] text-white shadow-sm"
                      : "bg-white text-brand-charcoal hover:bg-brand-sand-100 border border-brand-sand-200"
                  }`}
                >
                  {sub.heading}
                </Link>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {gallery.images.map((img, idx) => (
              <div
                key={idx}
                onClick={() => {
                  setLightboxImage(img.src);
                  setLightboxIndex(idx);
                }}
                className="group aspect-4/3 rounded-sm overflow-hidden bg-neutral-100 border border-brand-sand-200 shadow-xs hover:shadow-md transition-all duration-300 cursor-pointer relative"
              >
                <img
                  src={img.src.replace(
                    "https://assets.auxodev.dk/assets/",
                    "https://assets.auxodev.dk/cdn-cgi/image/width=600,height=450,fit=cover,quality=80,format=auto/assets/"
                  )}
                  alt={img.alt || `${gallery.heading} billede ${idx + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-white text-brand-green-900 flex items-center justify-center shadow-md">
                    <i className="fa-solid fa-magnifying-glass-plus text-sm" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Contact Box */}
          <div className="mt-20 bg-white border border-brand-sand-200 p-8 md:p-12 rounded-sm shadow-sm flex flex-col md:flex-row items-center justify-between gap-8 text-left">
            <div>
              <h2 className="font-display text-2xl font-bold text-brand-green-900 mb-2">
                Ønsker du et lignende resultat i din have?
              </h2>
              <p className="text-sm text-brand-charcoal/70 max-w-2xl leading-relaxed">
                Vi rådgiver om materialer, niveauforskelle, belægning og beplantning, så du får en skræddersyet løsning.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 shrink-0">
              <button
                onClick={openDrawer}
                className="px-6 py-3.5 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-xs uppercase tracking-wider rounded-sm transition-all duration-300 cursor-pointer shadow-sm"
              >
                Få et tilbud
              </button>
              <Link
                to="/kontakt"
                className="px-6 py-3.5 bg-brand-green-900 hover:bg-brand-green-800 text-white font-bold text-xs uppercase tracking-wider rounded-sm transition-all duration-300 shadow-sm"
              >
                Kontakt os
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm transition-opacity duration-300 p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-6 right-6 text-white hover:text-brand-sand-300 text-2xl p-2 cursor-pointer z-50"
            aria-label="Luk billede"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {gallery.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const prevIdx = (lightboxIndex - 1 + gallery.images.length) % gallery.images.length;
                setLightboxIndex(prevIdx);
                setLightboxImage(gallery.images[prevIdx].src);
              }}
              className="absolute left-6 text-white hover:text-brand-sand-300 text-3xl p-4 cursor-pointer z-50"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left" />
            </button>
          )}

          {gallery.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const nextIdx = (lightboxIndex + 1) % gallery.images.length;
                setLightboxIndex(nextIdx);
                setLightboxImage(gallery.images[nextIdx].src);
              }}
              className="absolute right-6 text-white hover:text-brand-sand-300 text-3xl p-4 cursor-pointer z-50"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right" />
            </button>
          )}

          <div
            className="max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.replace(
                "https://assets.auxodev.dk/assets/",
                "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/"
              )}
              alt="Forstørret billede"
              className="max-w-full max-h-[75vh] object-contain rounded-sm shadow-2xl"
            />
            <div className="mt-4 text-center text-white">
              <p className="text-sm font-medium">
                {gallery.images[lightboxIndex]?.alt || gallery.heading}
              </p>
              <p className="text-xs text-neutral-400 mt-1">
                {lightboxIndex + 1} af {gallery.images.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
